import { useState } from 'react';
import type { SoluuDevice, WiFiStatus } from '@soluu/ble-core';

import { ConnectGate } from '../components/ConnectGate.js';
import { DeviceInfoPanel } from '../components/DeviceInfoPanel.js';
import { DiagnosticsView } from '../components/DiagnosticsView.js';
import { ExpertApForm } from '../components/ExpertApForm.js';
import { LogActions } from '../components/LogActions.js';
import { LogList } from '../components/LogList.js';
import { PiStatusIndicator } from '../components/PiStatusIndicator.js';
import { RunModeToggle } from '../components/RunModeToggle.js';
import { ScenarioSelector } from '../components/ScenarioSelector.js';
import { StatusTimeline } from '../components/StatusTimeline.js';
import { UnsupportedBrowser } from '../components/UnsupportedBrowser.js';
import { WifiConfigForm } from '../components/WifiConfigForm.js';
import { useAttempt, type StartAttemptParams } from '../hooks/useAttempt.js';
import { usePiBackend } from '../hooks/usePiBackend.js';
import { isWebBluetoothAvailable } from '../core/webBluetooth.js';
import { useDevice } from '../state/DeviceProvider.js';
import { useSettings } from '../state/SettingsProvider.js';
import type { RunMode } from '../core/types.js';
import type { ScenarioListItem } from '../lib/piClient.js';

/**
 * WPA passphrase the Pi broadcasts with on every scenario. The harness
 * owns the AP, so this is a known constant rather than something the
 * tester has to type per attempt. Used both for `/ap/activate` and to
 * prefill the WiFi form's password field in Device + AP mode.
 */
const AP_PASSWORD = 'soluutest123';

export function HomePage() {
  const { mode } = useSettings();
  const ble = useDevice();

  if (!isWebBluetoothAvailable()) {
    return (
      <main className="main">
        <UnsupportedBrowser />
      </main>
    );
  }

  return (
    <main className="main">
      {mode === 'device+ap' && <ApModeWarning />}
      {mode === 'device+ap' && <ApModeBar />}
      <ConnectGate />
      {ble.device && (
        <Connected
          mode={mode}
          device={ble.device}
          status={ble.status}
        />
      )}
      <DiagnosticsView />
      <section className="section">
        <h2>Session Log</h2>
        <LogList />
        <LogActions />
      </section>
    </main>
  );
}

function ApModeWarning() {
  return (
    <div className="banner banner-mode" role="alert">
      <strong>Bench-test mode (Device + AP).</strong> The harness is set to
      drive a Pi access-point rig. Pi-broadcast networks usually have no
      internet, so devices typically stall at <code>CONNECTED</code> and
      will not reach <code>ON_INTERNET</code> in this mode — that's
      expected here, not a Soluu fault.{' '}
      <a href="#/settings">Switch to Device mode</a> for normal field tests
      against a real network.
    </div>
  );
}

function ApModeBar() {
  const { piUrl } = useSettings();
  const { health, version, apStatus, lastError } = usePiBackend(piUrl, true);
  return (
    <section className="section">
      <h2>Pi Backend</h2>
      <PiStatusIndicator
        baseUrl={piUrl}
        health={health}
        version={version}
        apStatus={apStatus}
        lastError={lastError}
      />
      <p className="muted" style={{ marginTop: '0.5rem' }}>
        Configure the URL in <a href="#/settings">Settings</a>.
      </p>
    </section>
  );
}

interface ConnectedProps {
  mode: 'device' | 'device+ap';
  device: SoluuDevice;
  status: WiFiStatus | null;
}

function Connected({ mode, device, status }: ConnectedProps) {
  const attempt = useAttempt(device, status);

  if (mode === 'device') {
    return (
      <DeviceModeBody
        device={device}
        status={status}
        attempt={attempt}
      />
    );
  }
  return (
    <DeviceApModeBody
      device={device}
      status={status}
      attempt={attempt}
    />
  );
}

interface BodyProps {
  device: SoluuDevice;
  status: WiFiStatus | null;
  attempt: ReturnType<typeof useAttempt>;
}

function DeviceModeBody({ device, status, attempt }: BodyProps) {
  const { statusEndpoint } = useSettings();
  const submit = (ssid: string, password: string) => {
    attempt.start({
      mode: 'manual',
      scenario: null,
      scenarioConfig: null,
      ssid,
      password,
    });
    void device
      .configureAndConnect(ssid, password, { pingEndpoint: statusEndpoint })
      .catch(() => {
        /* if the GATT writes fail the live status will time out at 30s with
         * the last observed label; the failure shape ends up in the log */
      });
  };

  return (
    <>
      <DeviceInfoPanel device={device} status={status} />
      <WifiConfigForm
        device={device}
        busy={attempt.inFlight}
        onCredentialReadAnomaly={attempt.noteAnomaly}
        onSubmit={submit}
      />
      <StatusTimeline
        transitions={attempt.transitions}
        startedAtMs={attempt.startedAtMs}
        result={attempt.derived?.result}
        failureReason={attempt.derived?.failureReason}
        totalDurationMs={attempt.derived?.totalDurationMs}
        tConnectedMs={attempt.derived?.tConnectedMs ?? null}
        tOnInternetMs={attempt.derived?.tOnInternetMs ?? null}
        anomalies={attempt.derived?.anomalies ?? []}
      />
    </>
  );
}

function DeviceApModeBody({ device, status, attempt }: BodyProps) {
  const { piUrl, statusEndpoint } = useSettings();
  const pi = usePiBackend(piUrl, true);
  const [scenarioName, setScenarioName] = useState<string | null>(null);
  const [runMode, setRunMode] = useState<RunMode>('manual');
  const [busyScenario, setBusyScenario] = useState(false);
  const [piError, setPiError] = useState<string | null>(null);
  // Prefill for the WiFi form once an AP has been activated. The form
  // mirrors whatever SSID + password is currently broadcasting so the
  // tester doesn't have to retype constants on every attempt. Editing
  // these values is NOT exposed here — to change the broadcast AP,
  // pick a different scenario, or switch to Expert mode.
  const [presetSsid, setPresetSsid] = useState('');
  const [presetPassword, setPresetPassword] = useState('');
  const [suiteRunning, setSuiteRunning] = useState(false);
  // Expert mode (Custom AP) state. Snapshot of what was last activated
  // via /ap/custom so log entries can record the regulatory config.
  const [expertConfig, setExpertConfig] = useState<{
    country: string;
    channel: number;
    txPowerDbm: number;
    ieee80211d: boolean;
  } | null>(null);

  const selected: ScenarioListItem | null =
    pi.scenarios.find((s) => s.name === scenarioName) ?? null;

  const defaultSsidFor = (s: ScenarioListItem): string =>
    s.ssidOverride ?? `soluu-${s.name}`;

  const defaultPasswordFor = (s: ScenarioListItem): string =>
    s.passwordOverride ?? AP_PASSWORD;

  // The scenario controls are meaningless without a reachable Pi. Surface
  // *why* they're disabled rather than leaving an empty dropdown that reads
  // as "still loading".
  const scenarioDisabledReason =
    pi.health === 'down'
      ? `Pi health check failed — backend unreachable at ${piUrl}`
      : pi.health === 'unknown'
        ? 'Checking Pi backend…'
        : null;

  const activateOnPi = async (s: ScenarioListItem) => {
    setBusyScenario(true);
    setPiError(null);
    try {
      const ssid = defaultSsidFor(s);
      const password = defaultPasswordFor(s);
      await pi.client.activate({ scenario: s.name, ssid, password });
      setPresetSsid(ssid);
      setPresetPassword(password);
      setExpertConfig(null);
      return { ssid, password };
    } catch (cause) {
      setPiError(cause instanceof Error ? cause.message : String(cause));
      throw cause;
    } finally {
      setBusyScenario(false);
    }
  };

  // Expert mode: bring up a custom AP via /ap/custom, then prefill the
  // WiFi form so the operator just clicks Connect. No scenario name is
  // associated with the resulting attempt (LogEntry.scenario will be null).
  const activateCustomAp = async (req: {
    ssid: string;
    password: string;
    country: string;
    channel: number;
    txPowerDbm: number;
    ieee80211d: boolean;
  }) => {
    setBusyScenario(true);
    setPiError(null);
    try {
      await pi.client.custom(req);
      setPresetSsid(req.ssid);
      setPresetPassword(req.password);
      setScenarioName(null);
      setExpertConfig({
        country: req.country,
        channel: req.channel,
        txPowerDbm: req.txPowerDbm,
        ieee80211d: req.ieee80211d,
      });
    } catch (cause) {
      setPiError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusyScenario(false);
    }
  };

  const onSelectScenario = async (name: string) => {
    setScenarioName(name);
    const s = pi.scenarios.find((x) => x.name === name);
    if (!s) return;
    try {
      const { ssid, password } = await activateOnPi(s);
      if (runMode === 'scripted') {
        const promise = startAttempt(s, ssid, password, 'scripted');
        void device
          .configureAndConnect(ssid, password, { pingEndpoint: statusEndpoint })
          .catch(() => {});
        await promise;
      }
    } catch {
      /* error already surfaced via piError */
    }
  };

  const startAttempt = (
    s: ScenarioListItem,
    ssid: string,
    password: string,
    mode: RunMode,
  ) => {
    const params: StartAttemptParams = {
      mode,
      scenario: s.name,
      scenarioConfig: {
        country: s.country,
        channel: s.channel,
        txPowerDbm: s.txPowerDbm,
        ieee80211d: s.ieee80211d,
      },
      ssid,
      password,
    };
    return attempt.start(params);
  };

  const onManualSubmit = (ssid: string, password: string) => {
    // Manual mode: use the selected scenario's regulatory config. Expert
    // mode: use the last-activated custom AP's config. Both end up driving
    // the same configure-and-connect flow against the Soluu.
    if (runMode === 'expert') {
      if (!expertConfig) return;
      void attempt.start({
        mode: 'expert',
        scenario: null,
        scenarioConfig: expertConfig,
        ssid,
        password,
      });
    } else {
      if (!selected) return;
      void startAttempt(selected, ssid, password, 'manual');
    }
    void device
      .configureAndConnect(ssid, password, { pingEndpoint: statusEndpoint })
      .catch(() => {});
  };

  const onRunSuite = async () => {
    if (!window.confirm(`Run all ${pi.scenarios.length} scenarios in sequence?`)) return;
    setSuiteRunning(true);
    for (const s of pi.scenarios) {
      try {
        const { ssid, password } = await activateOnPi(s);
        const finished = startAttempt(s, ssid, password, 'scripted');
        device
          .configureAndConnect(ssid, password, { pingEndpoint: statusEndpoint })
          .catch(() => {
            /* the attempt will timeout / fail and produce an entry */
          });
        await finished;
      } catch {
        /* keep going through the suite even if one scenario errors */
      }
    }
    setSuiteRunning(false);
  };

  return (
    <>
      <DeviceInfoPanel device={device} status={status} />
      <section className="section">
        <h2>Scenario</h2>
        <div className="row" style={{ gap: '1.5rem', alignItems: 'flex-end' }}>
          {runMode !== 'expert' && (
            <div style={{ minWidth: '20rem', flex: '1 1 20rem' }}>
              <ScenarioSelector
                scenarios={pi.scenarios}
                value={scenarioName}
                onChange={(name) => void onSelectScenario(name)}
                disabled={busyScenario || attempt.inFlight || suiteRunning}
                disabledReason={scenarioDisabledReason}
              />
            </div>
          )}
          <RunModeToggle
            value={runMode}
            onChange={setRunMode}
            disabled={attempt.inFlight || suiteRunning}
          />
          {runMode === 'scripted' && (
            <button
              type="button"
              onClick={() => void onRunSuite()}
              title={scenarioDisabledReason ?? undefined}
              disabled={
                pi.scenarios.length === 0 ||
                attempt.inFlight ||
                suiteRunning ||
                busyScenario ||
                scenarioDisabledReason !== null
              }
            >
              {suiteRunning ? 'Running suite…' : 'Run Suite'}
            </button>
          )}
        </div>
        {piError && (
          <div className="banner error" style={{ marginTop: '0.75rem' }}>
            {piError}
          </div>
        )}
      </section>
      {runMode === 'expert' && (
        <ExpertApForm
          busy={busyScenario || attempt.inFlight}
          errorMessage={null}
          onActivate={(req) => void activateCustomAp(req)}
        />
      )}
      {(runMode === 'manual' || runMode === 'expert') && (
        <WifiConfigForm
          // Key on the prefill so the form remounts when a new AP is
          // activated (scenario change or custom-AP reactivate). Avoids
          // the form keeping stale values across activations.
          key={`${presetSsid}::${presetPassword}`}
          device={device}
          initialSsid={presetSsid}
          initialPassword={presetPassword}
          busy={attempt.inFlight}
          onCredentialReadAnomaly={attempt.noteAnomaly}
          disabledReason={
            runMode === 'expert' && !expertConfig
              ? 'Activate a custom AP above before submitting.'
              : runMode === 'manual' && !selected
                ? 'Pick a scenario above before submitting.'
                : null
          }
          onSubmit={onManualSubmit}
        />
      )}
      <StatusTimeline
        transitions={attempt.transitions}
        startedAtMs={attempt.startedAtMs}
        result={attempt.derived?.result}
        failureReason={attempt.derived?.failureReason}
        totalDurationMs={attempt.derived?.totalDurationMs}
        tConnectedMs={attempt.derived?.tConnectedMs ?? null}
        tOnInternetMs={attempt.derived?.tOnInternetMs ?? null}
        anomalies={attempt.derived?.anomalies ?? []}
      />
    </>
  );
}

